import type { ReactNode } from 'react';
import { TrendingUp, TrendingDown } from 'lucide-react';
import { Card } from './Card';

type MetricStatus = 'good' | 'warning' | 'critical';

interface MetricCardProps {
  icon: ReactNode;
  label: string;
  value: string | number;
  unit?: string;
  trend?: number;
  status?: MetricStatus;
  onClick?: () => void;
  className?: string;
}

const statusColors: Record<MetricStatus, string> = {
  good: 'text-text-primary',
  warning: 'text-text-secondary',
  critical: 'text-text-primary',
};

const statusBorders: Record<MetricStatus, string> = {
  good: 'border-border',
  warning: 'border-border-light',
  critical: 'border-primary',
};

export function MetricCard({
  icon,
  label,
  value,
  unit,
  trend,
  status = 'good',
  onClick,
  className = '',
}: MetricCardProps) {
  const trendColor = trend && trend > 0 ? 'text-text-primary' : 'text-text-secondary';
  const TrendIcon = trend && trend > 0 ? TrendingUp : TrendingDown;

  return (
    <Card
      variant={onClick ? 'hover' : 'default'}
      padding="md"
      corners="asymmetric"
      onClick={onClick}
      className={`animate-slide-up ${statusBorders[status]} ${className}`}
    >
      <div className="flex items-start justify-between">
        <div className="flex-1">
          {/* Icon container - asymmetric inverse */}
          <div className="mb-3 inline-flex h-9 w-9 items-center justify-center rounded-asymmetric-inverse-md bg-surface-hover text-text-primary border border-border">
            {icon}
          </div>

          {/* Label */}
          <div className="mb-2 text-xs font-medium uppercase tracking-wider text-text-muted font-mono">
            {label}
          </div>

          {/* Value */}
          <div className="flex items-baseline gap-2">
            <span className={`text-4xl font-bold ${statusColors[status]} font-mono`}>
              {value}
            </span>
            {unit && (
              <span className="text-xl text-text-muted font-mono">{unit}</span>
            )}
          </div>
        </div>

        {/* Trend indicator */}
        {trend !== undefined && (
          <div className={`flex items-center gap-1 ${trendColor} font-mono text-sm font-semibold`}>
            <TrendIcon className="h-4 w-4" />
            <span>{Math.abs(trend)}%</span>
          </div>
        )}
      </div>
    </Card>
  );
}
